import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Header } from '../components/Header';
import { Loading } from '../components/Loading';
import MyReactTable from '../components/ReactTable';
import { TextOnly, Text, LangDictKey } from '../components/Text';
import Dialog from '@reach/dialog';
import { useUser } from '../context/User';
import { useFormik } from 'formik';
import { FilterPills } from '../components/FilterPills';
import { Row } from 'react-table';
import {
  toolAdminAddProvisioningToken,
  toolAdminGetProvisioningTokenList,
  toolAdminUpdateProvisioningToken,
} from '../libs/db-lib';
import { TOKEN_STATE_TYPE, TOKEN_STATE_TYPE_LIST } from '../CONSTANTS';
import { formatDateTime, formatDateTime2Lines } from '../libs/utils';
import CopyData from '../components/CopyData';
import { toast } from 'react-toastify';
import BasicDatePicker from '../components/BasicDatePicker';

export const ToolProvisioning = () => {
  const { user, vendorID } = useUser();

  const [search, setSearch] = useState<string>('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const [provisioningTokenList, setProvisioningTokenList] = useState<any[]>([]);

  const [selectedToken, setSelectedToken] = useState<any>(null);
  const [tokenName, setTokenName] = useState<string>('');
  const [daysToExpire, setDaysToExpire] = useState<number | undefined>(0);
  const [tokenState, setTokenState] = useState<any>('');
  const [countLimit, setCountLimit] = useState<number | undefined>(0);
  const [isValidSubmit, setIsValidSubmit] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);

  const [showTokenInfoDialog, setShowTokenInfoDialog] =
    useState<boolean>(false);

  const toggleTokenInfoDialog = () => {
    setShowAddEditTokenDialog(false);
    setShowFilterDialog(false);
    setShowTokenInfoDialog((prev) => !prev);
  };

  const [showAddEditTokenDialog, setShowAddEditTokenDialog] =
    useState<boolean>(false);

  const toggleAddEditTokenDialog = () => {
    setShowTokenInfoDialog(false);
    setShowFilterDialog(false);
    setShowAddEditTokenDialog((prev) => !prev);
  };

  const handleCloseAddEditTokenDialog = () => {
    toggleAddEditTokenDialog();
    setSelectedToken(null);
    setTokenName('');
    setTokenState('');
    setDaysToExpire(0);
    setCountLimit(0);
  };

  const handleSubmit = async () => {
    if (isValidSubmit) {
      setIsLoadingSubmit(true);

      let response;

      if (!selectedToken) {
        response = await toolAdminAddProvisioningToken({
          tokenName: tokenName,
          daysExpire: daysToExpire,
          initialCount: countLimit,
          userID: user?.userID,
          vendorID: vendorID,
          vendorAutoAuthUUID: user?.vendorAutoAuthUUID,
          userType: user?.userType,
        });
      } else {
        response = await toolAdminUpdateProvisioningToken({
          userID: user?.userID,
          userType: user?.userType,
          tokenID: selectedToken?.tokenID,
          tokenName: tokenName,
          tokenState: tokenState,
          countLimit: countLimit,
          vendorID: vendorID,
        });
      }

      if (response?.error) {
        toast.error(response.error, {
          autoClose: false,
          containerId: 'standard',
        });
      } else {
        if (!selectedToken) {
          toast.success(TextOnly('addTokenSuccess'), {
            containerId: 'standard',
          });

          const newToken = getProvisioningTokenList({
            tokenList: [{ ...response }],
          })[0];

          setProvisioningTokenList((prev) => [...prev, newToken]);
        } else {
          toast.success(TextOnly('updateTokenSuccess'), {
            containerId: 'standard',
          });

          setProvisioningTokenList((prev) =>
            prev
              .map((token) => {
                if (token?.tokenID === selectedToken?.tokenID) {
                  return getProvisioningTokenList({
                    tokenList: [
                      {
                        ...token,
                        tokenName: tokenName,
                        tokenState: tokenState,
                        countLimit: countLimit,
                      },
                    ],
                  })[0];
                } else {
                  return token;
                }
              })
              .filter((token: any) => token?.tokenState !== 'REMOVED')
          );
        }

        handleCloseAddEditTokenDialog();
      }

      setIsLoadingSubmit(false);
    }
  };

  const [isValidFilters, setIsValidFilters] = useState<boolean>(false);

  const [tokenExpireDateFilter, setTokenExpireDateFilter] =
    useState<string>('');
  const [tokenCountLimitFilter, setTokenCountLimitFilter] = useState<
    { min: number; max: number } | undefined
  >();
  const [tokenStateFilter, setTokenStateFilter] = useState<
    TOKEN_STATE_TYPE[] | undefined
  >([]);

  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);

  const toggleFilterDialog = () => {
    setShowTokenInfoDialog(false);
    setShowAddEditTokenDialog(false);
    setShowFilterDialog((prev) => !prev);
  };

  const resetFilters = () => {
    setTokenExpireDateFilter('');
    setTokenCountLimitFilter(undefined);
    setTokenStateFilter(undefined);
    setShowFilterDialog(false);
  };

  const getProvisioningTokenList = (data: any) => {
    const tokenList = data?.tokenList
      ?.filter((token: any) => token?.tokenState !== 'REMOVED')
      .map((token: any) => {
        return {
          vendorID: token?.vendorID,
          tokenID: token?.tokenID,
          tokenJWT: token?.tokenJWT,
          tokenName: token?.tokenName,
          tokenExpireDate: token?.tokenExpireDate,
          countLimit: token?.countLimit,
          currentCount: token?.currentCount,
          createdBy: token?.createdBy,
          createdOn: token?.createdOn,
          tokenState: token?.tokenState,
          crud: (
            <div className="l-flex-end l-flex-auto">
              <button
                className="c-btn-icon"
                onClick={() => {
                  setSelectedToken(token);
                  toggleTokenInfoDialog();
                }}
              >
                <div className="c-btn__inner">
                  <i className="c-btn__icon fal fa-info-circle" />
                </div>
              </button>

              <button
                className="c-btn-icon"
                onClick={() => {
                  setSelectedToken(token);
                  toggleAddEditTokenDialog();
                }}
              >
                <div className="c-btn__inner">
                  <i className="c-btn__icon fal fa-edit" />
                </div>
              </button>
            </div>
          ),
        };
      });
    return tokenList;
  };

  const { data: tokenData, isLoading: isLoadingTokens } = useQuery(
    'toolAdminGetProvisioningTokenList',
    () =>
      toolAdminGetProvisioningTokenList({
        userID: user?.userID,
        userType: user?.userType,
      })
  );

  function dateFilter(
    rows: Array<Row>,
    columnId: string,
    filterValue: string
  ): Row[] {
    return rows.filter((row) => {
      return (
        formatDateTime2Lines(filterValue)['date'] ===
        formatDateTime2Lines(row.values[columnId])['date']
      );
    });
  }

  function countLimitFilter(
    rows: Array<Row>,
    columnId: string,
    filterValue: string
  ): Row[] {
    return rows.filter((row) => {
      const [min, max] = filterValue.split('-').map((value) => Number(value));
      return row.values[columnId] >= min && row.values[columnId] <= max;
    });
  }

  function multiStateFilter(
    rows: Array<Row>,
    columnId: string,
    filterValue: string
  ): Row[] {
    const filterValueArray = filterValue.split(',');
    return rows.filter((row) => {
      return filterValueArray.some((v) => v === row.values[columnId]);
    });
  }

  const columns = [
    {
      Header: TextOnly('tokenName'),
      accessor: 'tokenName',
      enableGrow: true,
    },
    {
      Header: TextOnly('expireDate'),
      accessor: 'tokenExpireDate',
      Cell: ({ row: { original } }: any) => {
        const dateObj = formatDateTime2Lines(original?.tokenExpireDate);
        return <div>{dateObj.date}</div>;
      },
      filter: dateFilter,
    },
    {
      Header: TextOnly('maxCount'),
      accessor: 'countLimit',
      filter: countLimitFilter,
    },
    {
      Header: TextOnly('currentCount'),
      accessor: 'currentCount',
    },
    {
      Header: TextOnly('addedBy'),
      accessor: 'createdBy',
    },
    {
      Header: TextOnly('addedOn'),
      accessor: 'createdOn',
      Cell: ({ row: { original } }: any) => {
        const dateObj = formatDateTime2Lines(original?.createdOn);
        return (
          <div>
            {dateObj.date}
            <br />
            {dateObj.time}
          </div>
        );
      },
      filter: dateFilter,
    },
    {
      Header: TextOnly('tokenState'),
      accessor: 'tokenState',
      Cell: ({ row: { original } }: any) => (
        <>
          {original?.tokenState === 'ACTIVE'
            ? TextOnly('active').toUpperCase()
            : original?.tokenState === 'INACTIVE'
            ? TextOnly('inactive').toUpperCase()
            : TextOnly('removed').toUpperCase()}
        </>
      ),
      filter: multiStateFilter,
    },
    {
      Header: '',
      accessor: 'crud',
      disableSortBy: true,
    },
  ];

  function handleFilterDelete(field: string, value: string) {
    if (field === 'tokenExpireDate') {
      setTokenExpireDateFilter('');
    } else if (field === 'countLimit') {
      setTokenCountLimitFilter(undefined);
    } else if (field === 'tokenState') {
      setTokenStateFilter((prev) =>
        prev?.filter((tokenState) => tokenState !== value)
      );
    }
  }

  const tokenExpireDateFilterSelections = tokenExpireDateFilter
    ? {
        tokenExpireDate: [
          {
            key: tokenExpireDateFilter,
            value: formatDateTime2Lines(tokenExpireDateFilter)['date'],
            label: formatDateTime2Lines(tokenExpireDateFilter)['date'],
            pillLabel: `${TextOnly('expireDate')}: ${
              formatDateTime2Lines(tokenExpireDateFilter)['date']
            }`,
          },
        ],
      }
    : null;

  const tokenCountLimitFilterSelections = tokenCountLimitFilter
    ? {
        countLimit: [
          {
            key: `${tokenCountLimitFilter?.min}-${tokenCountLimitFilter?.max}`,
            value: `${tokenCountLimitFilter?.min}-${tokenCountLimitFilter?.max}`,
            label: `${TextOnly('maxCount')}: ${tokenCountLimitFilter?.min} - ${
              tokenCountLimitFilter?.max
            }`,
            pillLabel: `${TextOnly('maxCount')}: ${
              tokenCountLimitFilter?.min
            } - ${tokenCountLimitFilter?.max}`,
          },
        ],
      }
    : null;

  const tokenStateFilterSelections =
    (tokenStateFilter || [])?.length > 0
      ? {
          tokenState: tokenStateFilter?.map((option) => ({
            key: option,
            value: option,
            label: option,
            pillLabel: option,
          })),
        }
      : null;

  const filters = {
    ...tokenExpireDateFilterSelections,
    ...tokenCountLimitFilterSelections,
    ...tokenStateFilterSelections,
  };

  const filterFormik = useFormik({
    initialValues: {
      tokenExpireDateFilter: tokenExpireDateFilter,
      tokenCountLimitFilter: tokenCountLimitFilter,
      tokenStateFilter: tokenStateFilter,
    },
    onSubmit: async (values) => {
      setTokenExpireDateFilter(values.tokenExpireDateFilter);
      setTokenCountLimitFilter(values.tokenCountLimitFilter);
      setTokenStateFilter(values.tokenStateFilter);
      toggleFilterDialog();
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    setProvisioningTokenList(
      getProvisioningTokenList(tokenData || { tokenList: [] })
    );
  }, [tokenData]);

  useEffect(() => {
    if (selectedToken) {
      setTokenName(selectedToken?.tokenName || '');
      setTokenState(selectedToken?.tokenState || '');
      setDaysToExpire(0);
      setCountLimit(selectedToken?.countLimit || 0);
    } else {
      setTokenName('');
      setTokenState('');
      setDaysToExpire(0);
      setCountLimit(0);
    }
  }, [selectedToken]);

  useEffect(() => {
    if (filterFormik.values.tokenCountLimitFilter) {
      const [min, max] = Object.values(
        filterFormik.values.tokenCountLimitFilter
      );
      setIsValidFilters(
        Number.isFinite(Number(min)) &&
          Number.isFinite(Number(max)) &&
          Number(min) >= 0 &&
          Number(max) >= 1 &&
          Number(min) < Number(max)
      );
    } else {
      setIsValidFilters(true);
    }
  }, [filterFormik.values.tokenCountLimitFilter]);

  useEffect(() => {
    const isValidTokenName =
      typeof tokenName === 'string' && tokenName.length > 0;
    const isValidCountLimit =
      typeof countLimit === 'number' &&
      Number.isInteger(countLimit) &&
      countLimit > 0;

    if (selectedToken) {
      const hasTokenNameChanged = selectedToken?.tokenName !== tokenName;
      const hasTokenStateChanged = selectedToken?.tokenState !== tokenState;
      const hasTokenCountLimitChanged =
        selectedToken?.countLimit !== countLimit;

      const isValidTokenState = ['ACTIVE', 'INACTIVE', 'REMOVED'].some(
        (state) => state === tokenState
      );

      if (
        (hasTokenNameChanged ||
          hasTokenStateChanged ||
          hasTokenCountLimitChanged) &&
        isValidTokenName &&
        isValidCountLimit &&
        isValidTokenState
      ) {
        setIsValidSubmit(true);
      } else {
        setIsValidSubmit(false);
      }
    } else {
      const isValidDaysToExpire =
        typeof daysToExpire === 'number' &&
        Number.isInteger(daysToExpire) &&
        daysToExpire > 0;
      if (isValidTokenName && isValidCountLimit && isValidDaysToExpire) {
        setIsValidSubmit(true);
      } else {
        setIsValidSubmit(false);
      }
    }
  }, [tokenName, tokenState, countLimit, daysToExpire]);

  if (isLoadingTokens) {
    return <Loading />;
  }

  return (
    <>
      <Header title={TextOnly('toolProvisioning')} />
      <div className="l-container">
        <div className="l-flex-wrap">
          <div className="l-flex">
            <button
              className="c-btn u-margin-right"
              onClick={toggleAddEditTokenDialog}
            >
              <div className="c-btn__inner">
                <i className="c-btn__icon fal fa-plus" />
                <Text tid="addToken" />
              </div>
            </button>
            {Object.keys(filters).length ? (
              <div className="l-flex-wrap u-margin-top-large">
                <div className="l-flex-wrap">
                  <button
                    className="c-btn-link-text u-margin-right"
                    onClick={resetFilters}
                  >
                    <div className="c-btn__inner">
                      <Text tid="resetFilters" />
                    </div>
                  </button>
                  <FilterPills
                    filters={filters}
                    onFilterClick={handleFilterDelete}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className="l-flex-between">
            <div className="l-flex-between">
              <button
                className="c-btn-outline u-margin-right"
                onClick={toggleFilterDialog}
              >
                <div className="c-btn__inner">
                  <Text tid="filterBy" />
                  <i className="c-btn__icon fas fa-caret-down" />
                </div>
              </button>
              <div className="c-field u-margin-bottom-none">
                <label
                  htmlFor="search"
                  className="c-field__label u-is-vishidden"
                >
                  <Text tid="search" />
                </label>
                <input
                  type="text"
                  id="search"
                  maxLength={50}
                  className="c-input"
                  placeholder={TextOnly('search')}
                  value={search}
                  onChange={handleChange}
                />
                <i className="c-field__input-icon fal fa-search" />
              </div>
            </div>
          </div>
        </div>

        <div className="u-margin-top-large">
          <MyReactTable
            columns={columns}
            data={provisioningTokenList}
            globalFilter={search}
            tableName="toolProvisioning"
            filter={[
              {
                columnId: 'tokenExpireDate',
                filter: tokenExpireDateFilter
                  ? tokenExpireDateFilter
                  : undefined,
              },
              {
                columnId: 'countLimit',
                filter: tokenCountLimitFilter
                  ? tokenCountLimitFilter?.min +
                    '-' +
                    tokenCountLimitFilter?.max
                  : undefined,
              },
              {
                columnId: 'tokenState',
                filter:
                  (tokenStateFilter || []).length > 0
                    ? tokenStateFilter?.join(',')
                    : undefined,
              },
            ]}
          />
        </div>

        <Dialog
          isOpen={showTokenInfoDialog}
          onDismiss={() => {
            toggleTokenInfoDialog();
            setSelectedToken(null);
          }}
          className="c-modal-slider"
          aria-label={TextOnly('tokenInfo')}
        >
          <button
            className="c-btn-icon c-modal-slider__close"
            onClick={() => {
              toggleTokenInfoDialog();
              setSelectedToken(null);
            }}
          >
            <div className="c-btn__inner">
              <i className="c-btn__icon fa fa-times" />
            </div>
          </button>
          <h1 className="c-modal__heading">
            <Text tid="tokenInfo" />
          </h1>

          <div className="c-modal__body">
            <div className="l-container-sm">
              <div className="c-field">
                <div className="l-flex-wrap">
                  <label className="c-field__label l-align-self-flex-end">
                    <Text tid="tokenJWT" />
                  </label>
                  <CopyData info={selectedToken?.tokenJWT} />
                </div>
                <div className="c-field__note word-break-break-all">
                  {selectedToken?.tokenJWT}
                </div>
              </div>
              <div className="c-field">
                <label className="c-field__label">
                  <Text tid="tokenID" />
                </label>
                <div className="c-field__note">{selectedToken?.tokenID}</div>
              </div>
              <div className="c-field">
                <label className="c-field__label">
                  <Text tid="tokenName" />
                </label>
                <div className="c-field__note">{selectedToken?.tokenName}</div>
              </div>
              <div className="c-field">
                <label className="c-field__label">
                  <Text tid="expireDate" />
                </label>
                <div className="c-field__note">
                  {formatDateTime(selectedToken?.tokenExpireDate)}
                </div>
              </div>
              <div className="c-field">
                <label className="c-field__label">
                  <Text tid="maxCount" />
                </label>
                <div className="c-field__note">{selectedToken?.countLimit}</div>
              </div>
              <div className="c-field">
                <label className="c-field__label">
                  <Text tid="currentCount" />
                </label>
                <div className="c-field__note">
                  {selectedToken?.currentCount}
                </div>
              </div>
              <div className="c-field">
                <label className="c-field__label">
                  <Text tid="addedBy" />
                </label>
                <div className="c-field__note">{selectedToken?.createdBy}</div>
              </div>
              <div className="c-field">
                <label className="c-field__label">
                  <Text tid="addedOn" />
                </label>
                <div className="c-field__note">
                  {formatDateTime(selectedToken?.createdOn)}
                </div>
              </div>
              <div className="c-field">
                <label className="c-field__label">
                  <Text tid="tokenState" />
                </label>
                <div className="c-field__note">{selectedToken?.tokenState}</div>
              </div>
            </div>
          </div>
        </Dialog>

        <Dialog
          isOpen={showAddEditTokenDialog}
          onDismiss={handleCloseAddEditTokenDialog}
          className="c-modal-slider"
          aria-label={
            !selectedToken ? TextOnly('addToken') : TextOnly('editToken')
          }
        >
          <button
            className="c-btn-icon c-modal-slider__close"
            onClick={handleCloseAddEditTokenDialog}
          >
            <div className="c-btn__inner">
              <i className="c-btn__icon fa fa-times" />
            </div>
          </button>
          <h1 className="c-modal__heading">
            {!selectedToken ? (
              <Text tid="addToken" />
            ) : (
              <>
                <Text tid="editToken" /> - {selectedToken?.tokenName}
              </>
            )}
          </h1>

          <div className="c-modal__body">
            <div className="l-container-sm">
              <div className="c-field">
                <label className="c-field__label">
                  <Text tid="tokenName" />
                </label>
                <input
                  type="text"
                  id="tokenName"
                  className="c-input"
                  placeholder={TextOnly('tokenName')}
                  value={tokenName}
                  onChange={(e) => {
                    setTokenName(e.target.value);
                  }}
                />
              </div>
              <div className="c-field">
                <label className="c-field__label">
                  {!selectedToken ? (
                    <Text tid="initialCountLimit" />
                  ) : (
                    <Text tid="maxCount" />
                  )}
                </label>
                <input
                  type="number"
                  id="maxCount"
                  className="c-input max-count"
                  placeholder={
                    !selectedToken
                      ? TextOnly('initialCountLimit')
                      : TextOnly('maxCount')
                  }
                  value={countLimit}
                  onChange={(e) => {
                    setCountLimit(Number(e.target.value || undefined));
                  }}
                />
              </div>
              {!selectedToken ? (
                <div className="c-field">
                  <label className="c-field__label">
                    <Text tid="daysToExpire" />
                  </label>
                  <input
                    type="number"
                    id="daysToExpire"
                    className="c-input max-count"
                    placeholder={TextOnly('daysToExpire')}
                    value={daysToExpire}
                    onChange={(e) => {
                      setDaysToExpire(Number(e.target.value || undefined));
                    }}
                  />
                </div>
              ) : (
                <>
                  <div className="c-field">
                    <label className="c-field__label">
                      <Text tid="tokenState" />
                    </label>
                    <div>
                      <label>
                        <input
                          type="radio"
                          name="tokenState"
                          value="ACTIVE"
                          checked={tokenState === 'ACTIVE'}
                          onChange={(e) => setTokenState(e.target.value)}
                        />
                        <span className="u-margin-left">
                          {TextOnly('active').toLocaleUpperCase()}
                        </span>
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="tokenState"
                          value="INACTIVE"
                          checked={tokenState === 'INACTIVE'}
                          onChange={(e) => setTokenState(e.target.value)}
                        />
                        <span className="u-margin-left">
                          {TextOnly('inactive').toLocaleUpperCase()}
                        </span>
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="tokenState"
                          value="REMOVED"
                          checked={tokenState === 'REMOVED'}
                          onChange={(e) => setTokenState(e.target.value)}
                        />
                        <span className="u-margin-left">
                          {TextOnly('removed').toLocaleUpperCase()}
                        </span>
                      </label>
                    </div>
                  </div>
                </>
              )}
              <div className="c-field l-flex-between">
                <button
                  className="c-btn-outline u-margin-right"
                  type="button"
                  onClick={handleCloseAddEditTokenDialog}
                  disabled={isLoadingSubmit}
                >
                  <Text tid="cancel" />
                </button>
                <button
                  className="c-btn"
                  type="submit"
                  disabled={!isValidSubmit || isLoadingSubmit}
                  onClick={handleSubmit}
                >
                  {!isLoadingSubmit ? (
                    !selectedToken ? (
                      <Text tid="addToken" />
                    ) : (
                      <Text tid="saveChanges" />
                    )
                  ) : !selectedToken ? (
                    <>
                      <Text tid="adding" />
                      ...
                    </>
                  ) : (
                    <>
                      <Text tid="saving" />
                      ...
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Dialog>

        <Dialog
          isOpen={showFilterDialog}
          onDismiss={toggleFilterDialog}
          className="c-modal-slider"
          aria-label={TextOnly('filters')}
        >
          <button
            className="c-btn-icon c-modal-slider__close"
            onClick={toggleFilterDialog}
          >
            <div className="c-btn__inner">
              <i className="c-btn__icon fa fa-times" />
            </div>
          </button>
          <h1 className="c-modal__heading">
            <Text tid="filters" />
          </h1>

          <div className="c-modal__body">
            <form onSubmit={filterFormik.handleSubmit}>
              <div className="l-container-sm">
                <div className="c-field">
                  <label className="c-field__label">
                    <Text tid="selectExpireDate" />
                  </label>
                  <BasicDatePicker
                    initialDate={
                      tokenExpireDateFilter ? tokenExpireDateFilter : null
                    }
                    onChange={(date: Date) => {
                      filterFormik.setFieldValue(
                        'tokenExpireDateFilter',
                        date.toISOString()
                      );
                    }}
                  />
                </div>
                <div className="c-field">
                  <label className="c-field__label">
                    <Text tid="selectMaxCountRange" />
                  </label>
                  <div className="l-flex-align-items-center l-flex-between">
                    <input
                      type="number"
                      id="minimumMaxCount"
                      className="c-input max-count u-margin-right"
                      value={filterFormik.values.tokenCountLimitFilter?.min}
                      onChange={(e) => {
                        filterFormik.setFieldValue('tokenCountLimitFilter', {
                          min: e.target.value,
                          max: filterFormik.values.tokenCountLimitFilter?.max,
                        });
                      }}
                    />
                    <Text tid="to" />
                    <input
                      type="number"
                      id="maximumMaxCount"
                      className="c-input max-count u-margin-left"
                      value={filterFormik.values.tokenCountLimitFilter?.max}
                      onChange={(e) => {
                        filterFormik.setFieldValue('tokenCountLimitFilter', {
                          min: filterFormik.values.tokenCountLimitFilter?.min,
                          max: e.target.value,
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="c-field">
                  <label className="c-field__label">
                    <Text tid="selectTokenState" />
                  </label>

                  {TOKEN_STATE_TYPE_LIST.filter(
                    (option) => option !== 'REMOVED'
                  ).map((option) => {
                    const isChecked =
                      filterFormik.values.tokenStateFilter?.includes(option);

                    return (
                      <label key={option} className="l-flex-align-items-center">
                        <input
                          type="checkbox"
                          name="tokenStateFilter"
                          value={option}
                          checked={isChecked}
                          onChange={(e) => {
                            const value = e.target.value;

                            let newSelection;

                            if (isChecked) {
                              newSelection =
                                filterFormik.values.tokenStateFilter?.filter(
                                  (option) => option !== value
                                );
                            } else {
                              newSelection = [
                                ...(filterFormik.values?.tokenStateFilter ||
                                  []),
                                value,
                              ];
                            }

                            filterFormik.setFieldValue(
                              'tokenStateFilter',
                              newSelection
                            );
                          }}
                        />
                        <span className="c-field__note u-margin-left">
                          {TextOnly(
                            option.toLowerCase() as LangDictKey
                          ).toUpperCase()}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="c-field l-flex-between">
                  <button
                    className="c-btn-outline u-margin-right"
                    type="button"
                    onClick={resetFilters}
                  >
                    <Text tid="resetFilters" />
                  </button>
                  <button
                    className="c-btn"
                    type="submit"
                    disabled={!isValidFilters}
                  >
                    <Text tid="applyFilters" />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Dialog>
      </div>
    </>
  );
};
